/* Contratos Zod do plano de staff/auth do backoffice (Epic 28 / ADR-0345).
   Compartilhados entre `admin-service` (validação de input) e `admin-web`
   (tipos de request/response). 28-04..09 estendem com companies/users/plans/etc. */
import { z } from 'zod';

/** 4-tier RBAC do backoffice (ADR-0345 §3). */
export const staffRoleSchema = z.enum(['super_admin', 'support', 'billing_ops', 'readonly']);
export type StaffRole = z.infer<typeof staffRoleSchema>;

/** Status de um operador. */
export const staffStatusSchema = z.enum(['active', 'suspended']);
export type StaffStatus = z.infer<typeof staffStatusSchema>;

/** Input do login de operador (`POST /v1/staff/login`). */
export const loginSchema = z.object({
  email: z.string().email().toLowerCase(),
  password: z.string().min(8).max(128),
});
export type LoginInput = z.infer<typeof loginSchema>;

/** DTO da sessão de staff devolvido por `/v1/staff/me` + `/login`. */
export const staffSessionDto = z.object({
  id: z.string().min(1),
  email: z.string().email(),
  role: staffRoleSchema,
});
export type StaffSessionDto = z.infer<typeof staffSessionDto>;

/* Gestão de staff (ADR-0709): super_admin cria/gere operadores pela UI autenticada
   (o bootstrap só semeia o 1º super_admin). MVP sem migração de schema — senha
   inicial definida pelo super_admin + reset (sem convite por email / mustChange). */

/** Piso da senha de operador (≥12 chars — OWASP). Espelha o `min(12)` do bootstrap. */
const STAFF_PASSWORD_MIN = 12;
const STAFF_PASSWORD_MAX = 200;

/** DTO de um operador na listagem de gestão — NUNCA inclui `passwordHash`. */
export const staffUserDto = z.object({
  id: z.string().min(1),
  email: z.string().email(),
  role: staffRoleSchema,
  status: staffStatusSchema,
  createdAt: z.string(),
});
export type StaffUserDto = z.infer<typeof staffUserDto>;

/* Listagem de staff paginada por cursor keyset (ADR-0709): a lista de operadores
   cresce sem teto, então o GET usa o mesmo contrato de página das empresas —
   `{ cursor?, limit }` na query e `{ items, nextCursor }` na resposta (espelha
   `listCompaniesQuery`/`cursorPageSchema`). */

/** Query de `GET /v1/admin/staff` — keyset pagination `(createdAt,id)`. */
export const listStaffQuery = z.object({
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ListStaffQuery = z.infer<typeof listStaffQuery>;

/** Resposta de `GET /v1/admin/staff` — página keyset (`items` + `nextCursor` opaco).
    `staffUserDto` continua SEM `passwordHash`. */
export const listStaffResponseDto = z.object({
  items: z.array(staffUserDto),
  nextCursor: z.string().nullish(),
});
export type ListStaffResponse = z.infer<typeof listStaffResponseDto>;

/** Body de `POST /v1/admin/staff` — cria operador com senha inicial. */
export const createStaffBody = z.object({
  email: z.string().email(),
  role: staffRoleSchema,
  password: z.string().min(STAFF_PASSWORD_MIN).max(STAFF_PASSWORD_MAX),
});
export type CreateStaffBody = z.infer<typeof createStaffBody>;

/** Body de `PATCH /v1/admin/staff/:id/role`. */
export const updateStaffRoleBody = z.object({ role: staffRoleSchema });
export type UpdateStaffRoleBody = z.infer<typeof updateStaffRoleBody>;

/** Body de `PATCH /v1/admin/staff/:id/status`. */
export const updateStaffStatusBody = z.object({ status: staffStatusSchema });
export type UpdateStaffStatusBody = z.infer<typeof updateStaffStatusBody>;

/** Lote local de status; papel e senha continuam operações deliberadamente individuais. */
export const STAFF_BULK_MAX_IDS = 100;
export const bulkStaffStatusBody = z.object({
  ids: z
    .array(z.string().min(1))
    .min(1)
    .max(STAFF_BULK_MAX_IDS)
    .refine((ids) => new Set(ids).size === ids.length, { message: 'duplicate_bulk_ids' }),
  status: staffStatusSchema,
});
export type BulkStaffStatusBody = z.infer<typeof bulkStaffStatusBody>;

export const bulkStaffStatusResponseDto = z.object({
  results: z.array(z.object({ id: z.string(), ok: z.boolean(), error: z.string().nullable() })),
});
export type BulkStaffStatusResponse = z.infer<typeof bulkStaffStatusResponseDto>;

/** Body de `POST /v1/admin/staff/:id/reset-password`. */
export const resetStaffPasswordBody = z.object({
  password: z.string().min(STAFF_PASSWORD_MIN).max(STAFF_PASSWORD_MAX),
});
export type ResetStaffPasswordBody = z.infer<typeof resetStaffPasswordBody>;
